import { ArrowRight, Star } from "lucide-react";
import { CATEGORIES } from "@/data/content";
import { formatBRL } from "@/lib/api";

const ACCENTS = {
  blue: { chip: "bg-[#0F294A]/10 text-[#0F294A]", dot: "bg-[#0F294A]", btn: "text-[#0F294A] hover:bg-[#0F294A]/5", ring: "group-hover:border-[#0F294A]/30" },
  amber: { chip: "bg-amber-100 text-amber-700", dot: "bg-amber-500", btn: "text-amber-700 hover:bg-amber-50", ring: "group-hover:border-amber-300" },
  emerald: { chip: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", btn: "text-emerald-700 hover:bg-emerald-50", ring: "group-hover:border-emerald-300" },
};

export default function ServicesSection({ services = [], onSelect }) {
  return (
    <section id="servicos" className="py-20 lg:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <p className="text-xs font-semibold tracking-wider uppercase text-emerald-600 mb-3">Catálogo de Serviços</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
            Tudo o que você precisa, em um só lugar
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Escolha o serviço, preencha seus dados em 2 minutos e deixe a burocracia com a gente.
          </p>
        </div>

        {services.some((s) => s.featured) && (
          <div className="mb-16" data-testid="featured-services">
            <div className="flex items-center gap-2 mb-6">
              <Star className="h-5 w-5 fill-amber-400 text-amber-500" />
              <h3 className="font-heading text-xl sm:text-2xl font-bold text-slate-900">Mais procurados</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
              {services.filter((s) => s.featured).slice(0, 4).map((s) => (
                <button
                  key={s.id}
                  data-testid={`featured-card-${s.id}`}
                  onClick={() => onSelect(s)}
                  className="group text-left bg-white rounded-xl border border-amber-200 p-5 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                >
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-600 mb-3"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" /> Mais procurado</span>
                  <p className="font-heading font-bold text-slate-900 leading-snug">{s.name}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <p className="font-heading font-extrabold text-lg text-slate-900">{formatBRL(s.amount)}</p>
                    <span className="inline-flex items-center gap-1 text-sm font-bold text-emerald-700 group-hover:gap-1.5 transition-all">Solicitar <ArrowRight className="h-4 w-4" /></span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {Object.entries(CATEGORIES).map(([key, cat]) => {
          const accent = ACCENTS[cat.accent];
          const items = services.filter((s) => s.category === key);
          return (
            <div key={key} className="mb-16 last:mb-0" data-testid={`service-group-${key}`}>
              <div className="flex items-start gap-4 mb-7">
                <span className="grid place-items-center h-12 w-12 rounded-xl text-white shrink-0" style={{ backgroundColor: cat.color }}>
                  <cat.icon className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-heading text-xl sm:text-2xl font-bold text-slate-900">{cat.label}</h3>
                  <p className="text-slate-500 text-sm sm:text-base">{cat.tagline}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
                {items.map((s) => (
                  <div
                    key={s.id}
                    data-testid={`service-card-${s.id}`}
                    className={`group bg-white rounded-2xl border border-slate-200 p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${accent.ring}`}
                  >
                    <span className={`inline-flex items-center gap-2 self-start rounded-full px-2.5 py-1 text-xs font-semibold mb-4 ${accent.chip}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${accent.dot}`} /> {cat.short}
                      {s.featured && <><Star className="h-3 w-3 fill-amber-400 text-amber-500 ml-1" /> <span className="text-amber-600">Mais procurado</span></>}
                    </span>
                    <h4 className="font-heading font-bold text-slate-900 text-lg leading-snug">{s.name}</h4>
                    <p className="mt-2 text-sm text-slate-500 leading-relaxed flex-1">{s.desc}</p>
                    <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                      <div>
                        <p className="text-[11px] text-slate-400 uppercase tracking-wide">Assessoria a partir de</p>
                        <p className="font-heading font-extrabold text-xl text-slate-900">{formatBRL(s.amount)}</p>
                      </div>
                      <button
                        data-testid={`select-service-${s.id}`}
                        onClick={() => onSelect(s)}
                        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors ${accent.btn}`}
                      >
                        Solicitar <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
